import { type ReactNode } from 'react';

import { useState } from 'react';

import { Avatar, Box, IconButton, Stack, Typography } from '@mui/material';

import AccountSheet, {
  type AccountView,
} from '@/karehero-app/access/AccountSheet';
import CircleSwitcher, {
  type CircleView,
} from '@/karehero-app/shell/CircleSwitcher';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import { APP_HEADER_H } from '@/karehero-app/shell/layout';

// The root-page navigation bar, shown on every primary tab destination (Home,
// Actions, Care …): logo + optional name on the left, an optional bell and
// account avatar on the right. The counterpart to AppBar — Navigation is the
// persistent top chrome for root pages (no back), AppBar is the contextual bar
// for pushed pages and flows (back + title). One component so every root
// header stays identical, and it owns the avatar styling (pass `avatarInitials`
// for the standard avatar, or `avatar` for a custom node) so it never lands as
// a per-instance sx at the call site.
const Navigation = ({
  title,
  onNotifications,
  divider = true,
  avatar,
  avatarInitials,
  account,
  onMenu,
  initialSwitcherOpen,
  initialCircleView,
  initialInviteOpen,
  initialCircle,
  onSwitch,
}: {
  // Name beside the logo (e.g. the care recipient, "Margaret"). Omit for
  // logo-only. It opens the switcher: the bar names one of the people they care
  // for, and everything under it belongs to that person.
  title?: string;
  /** Harness-only: draw the switcher open, for a flows page. */
  initialSwitcherOpen?: boolean;
  /** Harness-only: and open on one of its pages. */
  initialCircleView?: CircleView;
  /** Harness-only: and that circle with its invite sheet up. */
  initialInviteOpen?: boolean;
  /**
   * Harness-only: the bar already switched to somebody else.
   *
   * The name only. Everything under it is still the one set of fixtures, so a
   * flows page showing the switch shows the header it changes and says the rest
   * in a readme.
   */
  initialCircle?: string;
  /**
   * Someone else picked in the switcher, for an app where switching changes
   * every screen rather than only the name in the bar.
   */
  onSwitch?: (name: string) => void;
  // Bell tap. The bell only shows when provided; pass a no-op for an inert bell.
  onNotifications?: () => void;
  /** The line under the bar. Off where a scene runs up behind it. */
  divider?: boolean;
  /**
   * A menu button where the logo sits, for a screen that carries the logo in
   * the page instead: the chat Home concept, whose menu opens the
   * conversations. Without it the bar is unchanged.
   */
  onMenu?: () => void;
  // A custom avatar node, pinned top-right.
  avatar?: ReactNode;
  // Convenience: render the standard account avatar from initials.
  avatarInitials?: string;
  /**
   * What the settings sheet knows about them, and how it starts.
   *
   * The sheet itself is not optional. The access bar's avatar has always opened
   * something, because before there is an account a way out is the only thing
   * there is; in the app it was the one control on every screen that did
   * nothing, and it is the only thing up there that is about them rather than
   * about the person they care for. So the avatar opens it wherever this bar
   * is, and this prop only fills it in.
   */
  account?: {
    email?: string;
    onSignOut?: () => void;
    /** Jump-state for stories and handover minis: open on the sheet. */
    initialSheetOpen?: boolean;
    /** And on one of its rows. See AccountSheet's `initialView`. */
    initialView?: AccountView;
  };
}) => {
  const [sheet, setSheet] = useState(account?.initialSheetOpen ?? false);
  // Which of the people they care for the bar is naming, and the drawer the
  // name opens to change it. Held here because the switch is the bar's own:
  // every screen under it is about whoever it says.
  const [switcher, setSwitcher] = useState(initialSwitcherOpen ?? false);
  const [who, setWho] = useState<string | undefined>(initialCircle);
  const named = who ?? title;

  return (
    <Stack
      sx={{
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 3,
        minHeight: APP_HEADER_H,
        flexShrink: 0,
        ...(divider
          ? { borderBottom: 1, borderColor: 'custom.neutral.200' }
          : {}),
      }}
      direction='row'
    >
      <Stack
        sx={{ alignItems: 'center', minWidth: 0 }}
        direction='row'
        spacing={1.25}
      >
        {onMenu ? (
          <IconButton
            aria-label='Conversations'
            onClick={onMenu}
            // A circle the avatar's size, so the bar's two ends match.
            sx={{
              width: 36,
              height: 36,
              border: 1,
              borderColor: 'custom.neutral.200',
              bgcolor: 'background.paper',
            }}
          >
            <Icon icon='bars' fontSize={16} />
          </IconButton>
        ) : (
          <Box
            sx={{ width: 30, height: 'auto', flexShrink: 0 }}
            component='img'
            src='/vector/karehero-logo.png'
            alt='KareHero'
          />
        )}
        {named && (
          // Marine green and h4: the name sits beside the logo, so it reads as
          // part of the mark rather than as a page heading. primary.main is
          // MarineGreen[700], which is the logo's own colour.
          //
          // In sx, not Typography's `color`: llama bakes a literal colour into
          // every variant at import time, so the prop is inert here.
          // The caret is what says the name is a control. There is a menu
          // behind it now — the other people you care for, the circle around
          // this one, and starting another — and a bare name reads as a label.
          <Stack
            sx={{
              alignItems: 'center',
              minWidth: 0,
              cursor: 'pointer',
              color: 'primary.main',
            }}
            direction='row'
            spacing={0.5}
            onClick={() => setSwitcher(true)}
          >
            <Typography sx={{ color: 'primary.main' }} variant='h4' noWrap>
              {named}
            </Typography>
            {/* chevron-down, because llama has no caret: the icon set is
                Font Awesome light and caret-down is not registered in it, so
                the caret rendered nothing at all. */}
            <Icon icon='chevron-down' fontSize={12} />
          </Stack>
        )}
      </Stack>
      <Stack
        sx={{ alignItems: 'center', flexShrink: 0 }}
        direction='row'
        spacing={1}
      >
        {onNotifications && (
          // The back chevron's circle, so every round button in a bar looks
          // the same, at the avatar's 36px so the pair match. The tap area
          // still reaches 44px, past the circle.
          <IconButton
            aria-label='Notifications'
            onClick={onNotifications}
            variant='round'
            sx={{
              width: 36,
              height: 36,
              position: 'relative',
              '&::after': { content: '""', position: 'absolute', inset: -4 },
            }}
          >
            <Icon icon='bell' fontSize={16} />
          </IconButton>
        )}
        {avatar ??
          (avatarInitials && (
            <Avatar
              onClick={() => setSheet(true)}
              sx={{
                width: 36,
                height: 36,
                bgcolor: 'custom.neutral.400',
                color: 'custom.neutral.50',
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              {avatarInitials}
            </Avatar>
          ))}

        {/* Who the app is about, from the name it says. */}
        {switcher && (
          <CircleSwitcher
            current={named}
            onSwitch={(name) => {
              setWho(name);
              onSwitch?.(name);
            }}
            initialView={initialCircleView}
            initialInviteOpen={initialInviteOpen}
            onClose={() => setSwitcher(false)}
          />
        )}

        {/* The fuller sheet: in the app there is an account to manage, and a
          circle. See AccountSheet's `inApp`. */}
        {sheet && (
          <AccountSheet
            inApp
            initialView={account?.initialView}
            email={account?.email}
            onSignOut={account?.onSignOut}
            onClose={() => setSheet(false)}
          />
        )}
      </Stack>
    </Stack>
  );
};

Navigation.displayName = 'Navigation';
export default Navigation;
